import dayjs from 'dayjs';
import numeral from 'numeral';
import { DateRangeOptions, DateRangeType } from 'src/constants';
import {
  UtilityType,
  UtilityTypeOrAll,
  convertToAndFromBase,
} from 'switch-shared/modules/crud/utility/utility.enums';
import { Parameter } from 'switch-shared/types/parameter.enums';
import { CachedShadowState } from 'switch-shared/utils/iot-shadow-states';

import { isAllUtilitySelection } from './selection';

export const getEllipsisTxt = (str: string, n = 6) => {
  if (str) {
    return `${str.substring(0, n)}...${str.substring(str.length - n)}`;
  }
  return '';
};

/**
 * Month select options with 0-11 values, matching the season entities and the
 * tariff engine.
 */
export const MONTH_OPTIONS = [...Array(12)].map((_, month) => ({
  value: month,
  label: dayjs().month(month).format('MMMM'),
}));

export const formatCurrency = (value: number, type = 'float') => {
  if (type === 'integer') {
    return numeral(value).format('R0,0');
  }

  return numeral(value).format('R0,0.00');
};

export const addSeparator = (num: string) =>
  num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const currencyFormat = (num: any, symbol: string) =>
  `${symbol} ${addSeparator(Number(num).toFixed(2))}`;

export const capitalize = (str = ''): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const camelCaseToSpaces = (input: string): string => {
  const spacedString = input.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spacedString;
};

export const formatReduxRTKMutation = (message: string) => {
  const formatCamelCase = camelCaseToSpaces(message);

  const actionMapping = {
    update: 'Updating ',
    create: 'Creating ',
    delete: 'Deleting ',
    read: 'Reading ',
    reveal: 'Revealing ',
    revoke: 'Revoking ',
    restore: 'Restoring ',
    generate: 'Generating ',
    email: 'Emailing ',
    sync: 'Syncing ',
    reset: 'Resetting ',
    rollback: 'Rolling back ',
    download: 'Downloading ',
    link: 'Linking ',
    remove: 'Removing ',
    verify: 'Verifying ',
    send: 'Sending ',
    request: 'Requesting',
    export: 'Exporting',
    replace: 'Replacing',
    unLink: 'Unlinking',
  };

  const foundAction = Object.keys(actionMapping).find((action) =>
    message.includes(action)
  );

  if (foundAction) {
    const updatedString = formatCamelCase.replace(
      foundAction,
      actionMapping[foundAction as keyof typeof actionMapping]
    );
    return updatedString;
  }

  return formatCamelCase;
};

export const formatNumber = (
  value: number,
  type = 'float',
  utilityType?: UtilityType
) => {
  if (utilityType) {
    switch (utilityType) {
      case UtilityType.WATER:
      case UtilityType.GAS:
      case UtilityType.ELECTRICITY:
        return Math.round((value / 1000) * 1000) / 1000;
      default:
        break;
    }
  }

  if (type === 'integer') {
    return numeral(value).format('0,0', Math.floor);
  }

  return numeral(value).format('0,0.00', Math.floor);
};

export const roundUsage = (
  num: number,
  decimals: number,
  utilityType?: UtilityType
) => {
  const factorOfTen = Math.pow(10, decimals);
  return formatNumber(
    Math.round(num * factorOfTen) / factorOfTen,
    'float',
    utilityType
  );
};

export const getUtilityUnits = (
  utilityType?: UtilityTypeOrAll,
  kilo?: boolean,
  parameter?: Parameter
) => {
  if (isAllUtilitySelection(utilityType)) {
    return 'Units';
  }

  switch (utilityType) {
    case UtilityType.WATER:
      return `${kilo ? 'k' : ''}L`;

    case UtilityType.ELECTRICITY:
      if (parameter) {
        switch (parameter) {
          case Parameter.PMD:
            return `${kilo ? 'k' : ''}W`;
          case Parameter.QMD:
            return `${kilo ? 'k' : ''}VAR`;
          case Parameter.SMD:
            return `${kilo ? 'k' : ''}VA`;
          case Parameter.Q1:
          case Parameter.Q2:
            return `${kilo ? 'k' : ''}VARh`;

          default:
            break;
        }
      }
      return `${kilo ? 'k' : ''}Wh`;

    case UtilityType.GAS:
      return `${kilo ? '' : 'c'}m3`;

    default:
      return 'unit';
  }
};

export const getFlatRateUnit = (
  ratePerUnit: number,
  utilityType?: UtilityType,
  unit?: string
) => {
  if (unit) {
    return `R ${convertToAndFromBase(ratePerUnit, utilityType).toFixed(4)} per k${unit}`;
  } else {
    return `R ${convertToAndFromBase(ratePerUnit, utilityType).toFixed(4)} per ${getUtilityUnits(
      utilityType,
      true
    )}`;
  }
};

export const formatDateRange = (
  dateRangeFilter: DateRangeType,
  startDate: string,
  endDate: string
) => {
  const dateRangeInfo = DateRangeOptions.find(
    (ele) => ele._id === dateRangeFilter
  );

  if (!dateRangeInfo) {
    return 'Loading...';
  }

  if (dateRangeInfo.name === 'Custom') {
    return `${dayjs(startDate).format('D MMM YYYY')} to ${dayjs(endDate).format(
      'D MMM YYYY'
    )}`;
  }

  return dateRangeInfo.name;
};

export const getYAxisLabel = (type: UtilityType) => {
  if (type === UtilityType.WATER) {
    return 'Consumption (kL)';
  }
  if (type === UtilityType.ELECTRICITY) {
    return 'Consumption (kWh)';
  }

  if (type === UtilityType.GAS) {
    return 'Consumption (m3)';
  }
  return 'Consumption (Units)';
};

export const getIoTStateDescription = (
  iotState: CachedShadowState,
  relayParam?: Parameter.R | Parameter.R2 | Parameter.R3,
  utilityType?: UtilityType
) => {
  const param = relayParam ?? Parameter.R;
  const reported = iotState?.reported?.[param as Parameter.R];
  const desired = iotState?.desired?.[param as Parameter.R];
  const isWater = utilityType === UtilityType.WATER;

  if (reported == null) {
    return 'Awaiting initial read';
  }

  if (reported === desired || desired == null) {
    return reported === true
      ? isWater
        ? 'Open'
        : 'On'
      : isWater
        ? 'Closed'
        : 'Off';
  } else if (reported === false && desired === true) {
    return `Attempting to ${isWater ? 'open' : 'switch on'}`;
  } else if (reported === true && desired === false) {
    return `Attempting to ${isWater ? 'close' : 'switch off'}`;
  }
  return 'Unknown';
};

export const formatDateTime = (
  value: string | Date,
  includeSeconds = false
) => {
  return dayjs(value).format(
    includeSeconds ? 'DD/MM/YYYY HH:mm:ss' : 'DD/MM/YYYY HH:mm'
  );
};
